import { type Query, type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { editorApi, exportsApi, projectsApi } from '@/shared/api/endpoints';
import type { DocumentSavePayload } from '@/shared/api/types';

export const projectKeys = {
  all: ['projects'] as const,
  list: (filters: Record<string, unknown>) => ['projects', 'list', filters] as const,
  detail: (id: string) => ['projects', 'detail', id] as const,
  document: (id: string) => ['projects', 'document', id] as const,
  exports: (id: string) => ['projects', 'exports', id] as const,
  jobs: (id: string) => ['projects', 'jobs', id] as const,
  assets: (id: string) => ['projects', 'assets', id] as const,
};

const isDocument = (query: Query) => query.queryKey[0] === 'projects' && query.queryKey[1] === 'document';

/**
 * Refresh what is known about projects (lists, details, jobs, which videos became projects) after
 * something changed them, but never an open editor's document: the editor owns that once loaded,
 * and applies a job of its own through its own reload. Refetching it from here reset the editor
 * whenever any job anywhere finished.
 */
export function invalidateProjects(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: projectKeys.all, predicate: (query) => !isDocument(query) });
}

export const libraryKeys = {
  exports: (filters: Record<string, unknown>) => ['library', 'exports', filters] as const,
};

export function useProjects(filters: {
  page: number;
  page_size: number;
  search?: string;
  source_type?: string;
  sort?: 'activity' | 'created' | 'name';
}) {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: () => projectsApi.list(filters),
    staleTime: 30_000,
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(id ?? ''),
    queryFn: () => projectsApi.get(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useProjectDocument(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.document(id ?? ''),
    queryFn: () => projectsApi.document(id!),
    enabled: Boolean(id),
    // The editor owns the document once loaded; refetching under it would clobber edits.
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useProjectExports(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.exports(id ?? ''),
    queryFn: () => editorApi.exportJobs(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useProjectJobs(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.jobs(id ?? ''),
    queryFn: () => editorApi.jobs({ project_id: id!, page_size: 50 }),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useProjectAssets(id: string | undefined) {
  return useQuery({
    queryKey: projectKeys.assets(id ?? ''),
    queryFn: () => editorApi.audioAssets(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useRenameProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => projectsApi.rename(id, name),
    onSuccess: (_data, variables) => {
      void invalidateProjects(queryClient);
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
    },
  });
}

export function useDuplicateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.duplicate(id),
    onSuccess: () => invalidateProjects(queryClient),
  });
}

export function useExtendProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.extend(id),
    onSuccess: () => invalidateProjects(queryClient),
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => projectsApi.remove(id),
    onSuccess: () => {
      void invalidateProjects(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['library'] });
    },
  });
}

export function useSaveDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DocumentSavePayload }) =>
      projectsApi.saveDocument(id, payload),
    onSuccess: (document, variables) => {
      // Keep the cache in step, but do not invalidate — the editor is the owner of this data.
      queryClient.setQueryData(projectKeys.document(variables.id), document);
      void queryClient.invalidateQueries({ queryKey: projectKeys.all, exact: false, refetchType: 'none' });
    },
  });
}

export function useExportsLibrary(filters: { page: number; page_size: number; status?: string }) {
  return useQuery({
    queryKey: libraryKeys.exports(filters),
    queryFn: () => exportsApi.list(filters),
    staleTime: 20_000,
  });
}

export function useDeleteExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => exportsApi.remove(jobId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['library', 'exports'] });
      void invalidateProjects(queryClient);
    },
  });
}
